"use client";

import { Loader2 } from "lucide-react";
import { FormEvent, useRef, useState } from "react";

const inputClass =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[#b5e7e1] outline-none transition-all";
const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

const HEARD_ABOUT_OPTIONS = [
  "Word of mouth",
  "Facebook",
  "Instagram",
  "Google search",
  "Ofsted / Manchester City Council",
  "Other",
];

export default function RegistrationForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("Something went wrong. Please try again.");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("saving");
    setErrorMessage("Something went wrong. Please try again.");

    const formData = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/registration", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(formData.entries())),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(
          typeof data.error === "string" ? data.error : "Unable to submit your registration right now."
        );
        setStatus("error");
        return;
      }

      formRef.current?.reset();
      setStatus("success");
    } catch (error) {
      console.error("Registration submit failed:", error);
      setStatus("error");
    }
  }

  return (
    <div className="rounded-[2rem] border border-gray-100 bg-white p-8 shadow-xl">
      <h2 className="mb-6 text-2xl font-bold text-[var(--color-secondary)]">Register your child</h2>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="childName" className={labelClass}>Child&apos;s full name *</label>
            <input type="text" id="childName" name="childName" required className={inputClass} />
          </div>
          <div>
            <label htmlFor="childDob" className={labelClass}>Child&apos;s date of birth *</label>
            <input type="date" id="childDob" name="childDob" required className={inputClass} />
          </div>
        </div>

        <div>
          <label htmlFor="parentName" className={labelClass}>Parent&apos;s full name *</label>
          <input type="text" id="parentName" name="parentName" required className={inputClass} />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="parentPhone" className={labelClass}>Parent&apos;s phone number *</label>
            <input type="tel" id="parentPhone" name="parentPhone" required className={inputClass} />
          </div>
          <div>
            <label htmlFor="parentEmail" className={labelClass}>Parent&apos;s email</label>
            <input type="email" id="parentEmail" name="parentEmail" className={inputClass} />
          </div>
        </div>

        <div>
          <label htmlFor="sessionsRequired" className={labelClass}>Sessions/days required</label>
          <textarea
            id="sessionsRequired"
            name="sessionsRequired"
            rows={3}
            placeholder="e.g. Monday to Wednesday, 8am - 5pm"
            className={`${inputClass} resize-none`}
          />
        </div>

        <div>
          <label htmlFor="hearAboutUs" className={labelClass}>How did you hear about us? *</label>
          <select id="hearAboutUs" name="hearAboutUs" required defaultValue="" className={inputClass}>
            <option value="" disabled>Please choose</option>
            {HEARD_ABOUT_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={status === "saving"}
          className="flex w-full transform items-center justify-center gap-2 rounded-full bg-[var(--color-primary)] py-4 text-lg font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-[var(--color-primary-dark)] disabled:translate-y-0 disabled:opacity-70"
        >
          {status === "saving" && <Loader2 className="animate-spin" size={20} />}
          {status === "saving" ? "Submitting..." : "Submit registration"}
        </button>

        {status === "success" && (
          <p className="font-bold text-green-700">
            Thank you. We have your registration and will be in touch to arrange your visit.
          </p>
        )}
        {status === "error" && <p className="font-bold text-red-700">{errorMessage}</p>}
      </form>
    </div>
  );
}
